import { RefObject } from "react";
import classNames from "classnames";
import { motion } from "framer-motion";
import Wind from "components/icons/Wind";
import { ListIcon } from "components/icons/ListIcon";
import { CalendarBlank } from "components/icons/CalendarBlank";
import { Cart } from "components/icons/Cart";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { getFilingStatusTag } from "v2/utils/getFilingStatusTag";
import { FilingActionsMenu } from "v2/components/FilingsDashboard/FilingActionsMenu";
import { FilingNode, SeasonFilings } from "v2/types/filingsDashboard.types";
import { formatUsd } from "v2/utils/mapDetectedFilings";
import { isFilingFiledExternally } from "v2/utils/isFilingFiledExternally";
import { FLIGHT_POP_DURATION_MS } from "v2/utils/useFilingFlights";

type FilingsDetectedPanelProps = {
  seasonFilings: SeasonFilings[];
  count: number;
  entityId: string;
  listRef?: RefObject<HTMLDivElement | null>;
  landedFilingId?: string | null;
  pendingFilingIds?: string[];
};

const MS_IN_SECOND = 1000;
const EMPTY_PENDING_IDS: string[] = [];

const POP_SCALE = [0.92, 1.02, 1];
const POP_OPACITY = [0.45, 1, 1];

export const NoFilingEmptyState = () => (
  <div className="t-flex t-flex-1 t-flex-col t-items-center t-justify-center t-gap-3 t-px-6 t-text-center">
    <span className="t-text-neutral-40">
      <Wind />
    </span>

    <div className="t-flex t-flex-col t-items-center t-gap-2">
      <span className="t-text-body-sm t-font-medium t-text-text-30">
        No filings detected
      </span>
      <span className="t-text-body-sm t-font-light t-text-neutral-40">
        Start filling the details and we&apos;ll auto-detect the filings
        applicable to this entity
      </span>
    </div>
  </div>
);

const seasonHeading = (season: string): string =>
  /^\d{4}$/.test(season) ? `SEASON ${season}` : season.toUpperCase();

const SeasonHeader = ({ season }: { season: string }) => (
  <div className="t-bg-surface-lighter-grey t-px-4 t-py-2">
    <span className="t-text-caption t-uppercase t-text-text-30">
      {seasonHeading(season)}
    </span>
  </div>
);

const FilingRow = ({
  filing,
  entityId,
  season,
  isLanded,
  isInFlight,
}: {
  filing: FilingNode;
  entityId: string;
  season: string;
  isLanded?: boolean;
  isInFlight?: boolean;
}) => {
  const isFiledExternally = isFilingFiledExternally(filing.status);
  const shouldShowDueDate = Boolean(filing.dueDate) && !isFiledExternally;

  const statusTag = getFilingStatusTag(filing.status);

  return (
    <motion.div
      data-filing-id={filing.id}
      animate={
        isLanded ? { scale: POP_SCALE, opacity: POP_OPACITY } : { scale: 1 }
      }
      transition={{ duration: FLIGHT_POP_DURATION_MS / MS_IN_SECOND }}
      className={classNames(
        "t-flex t-items-start t-justify-between t-gap-4 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-p-4",
        { "!t-hidden": isInFlight }
      )}
    >
      <div className="t-flex t-flex-col t-gap-0.5">
        <span className="t-text-body t-text-text-100">{filing.title}</span>

        {shouldShowDueDate && (
          <div className="t-flex t-items-center t-gap-1.5">
            <span className="t-flex t-text-text-30">
              <CalendarBlank />
            </span>
            <span className="t-text-body-sm t-text-text-30">
              {filing.dueDate}
            </span>
          </div>
        )}

        {filing.description && (
          <span className="t-text-body-sm t-text-neutral-50">
            {filing.description}
          </span>
        )}
      </div>

      <div className="t-flex t-shrink-0 t-items-center t-gap-2">
        {statusTag ? (
          <Tag tagType={statusTag.tagType} rounded size="small" icon>
            {statusTag.label}
          </Tag>
        ) : (
          <div className="t-flex t-items-center t-gap-1.5">
            <span className="t-flex t-text-text-30">
              <Cart />
            </span>
            <span className="t-text-body-sm t-text-text-30">
              {formatUsd(filing.paymentAmount)}
            </span>
          </div>
        )}

        {!isFiledExternally && (
          <FilingActionsMenu
            filing={filing}
            entityId={entityId}
            season={season}
          />
        )}
      </div>
    </motion.div>
  );
};

export const FilingsGroupBySeason = ({
  seasonFilings,
  entityId,
  listRef,
  landedFilingId,
  pendingFilingIds = EMPTY_PENDING_IDS,
}: {
  seasonFilings: SeasonFilings[];
  entityId: string;
  listRef?: RefObject<HTMLDivElement | null>;
  landedFilingId?: string | null;
  pendingFilingIds?: string[];
}) => {
  return (
    <div
      ref={listRef}
      className="scrollbar-thin t-flex t-flex-1 t-flex-col t-overflow-y-auto"
    >
      {seasonFilings.map((group) => (
        <div key={group.season} className="t-flex t-flex-col">
          <SeasonHeader season={group.season} />

          {group.filings.map((filing) => (
            <FilingRow
              key={filing.id}
              filing={filing}
              entityId={entityId}
              season={group.season}
              isLanded={filing.id === landedFilingId}
              isInFlight={pendingFilingIds.includes(filing.id)}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export const FilingsDetectedPanel = ({
  seasonFilings,
  count,
  entityId,
  listRef,
  landedFilingId,
  pendingFilingIds,
}: FilingsDetectedPanelProps) => {
  const hasFilings = seasonFilings.length > 0;

  const headingTitle =
    count > 0 ? `Filings detected (${count})` : "Filings detected";

  return (
    <div className="t-flex t-h-full t-flex-col t-overflow-hidden t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-surface">
      <div className="t-flex t-shrink-0 t-items-start t-gap-2 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-p-4">
        <span className="t-flex t-rounded t-bg-surface-purple t-p-1 t-text-purple">
          <ListIcon />
        </span>

        <div className="t-flex t-flex-col t-gap-0.5">
          <span className="t-text-subtext t-text-text-60">{headingTitle}</span>
          <span className="t-text-body-sm t-italic t-text-neutral-40">
            Updates as you enter the details
          </span>
        </div>
      </div>

      {!hasFilings && <NoFilingEmptyState />}

      {hasFilings && (
        <FilingsGroupBySeason
          entityId={entityId}
          seasonFilings={seasonFilings}
          listRef={listRef}
          landedFilingId={landedFilingId}
          pendingFilingIds={pendingFilingIds}
        />
      )}
    </div>
  );
};
